<template>
    <v-footer
        class="order-footer"
        color="#282b38"
        fixed
        app
        dark
    >
        <div class="summary">
            <div class="summary-item">
                <span>{{ $t('general.total') }}</span>
                <span v-if="curOrder">{{ curOrder.getPrice().toFormat('0.00') }}</span>
            </div>
            <div class="summary-item">
                <span>{{ $t('general.discount') }}</span>
                <span v-if="curOrder">{{ curOrder.getDiscountAmount().toFormat('0.00') }}</span>
            </div>
            <div v-if="curOrder && curOrder.getPaidAdvance().toUnit() > 0" class="summary-item">
                <span class="advance">{{ $t('general.advance') }}</span>
                <div>
                    <span v-if="curOrder.getPaidAdvanceEffective().toUnit() != curOrder.getPaidAdvance().toUnit()">
                        {{ curOrder.getPaidAdvance().toFormat('0.00') }} |
                    </span>
                    <span class="advance">{{ curOrder.getPaidAdvanceEffective().toFormat('0.00') }}</span>
                </div>
            </div>
            <div class="summary-item">
                <span>{{ $t('general.to_pay') }}</span>
                <span v-if="curOrder" class="to-pay">{{ curOrder.getLeftToPay().toFormat('0.00') }}</span>
            </div>
        </div>

        <v-bottom-navigation
            :v-model="bottomNav"
            background-color="transparent"
            height="80px"
            grow
        >
            <v-btn
                v-for="option in options.primary"
                :key="option.value"
                class="bottom-nav-item"
                :class="option.classes"
                text
                :value="option.value"
                :disabled="option.disabled"
                @click="selectOption(option.value)"
            >
                <span>{{ option.text }}</span>
                <v-icon>{{ option.icon }}</v-icon>
            </v-btn>
        </v-bottom-navigation>

        <v-bottom-sheet v-model="showBottomSheet">
            <div class="order-options">
                <template v-for="(optionsSection, index) in options.secondary">
                    <v-list
                        v-if="optionsSection.items.length"
                        :key="index"
                    >
                        <v-subheader>{{ optionsSection.title }}</v-subheader>

                        <v-list-item
                            v-for="option in optionsSection.items"
                            :key="option.value"
                            class="bottom-sheet-item"
                            :class="option.classes"
                            :disabled="option.disabled"
                            @click="selectOption(option.value)"
                        >
                            <v-list-item-avatar
                                class="mr-3"
                                :class="{ 'icon-disabled': option.disabled }"
                            >
                                <v-avatar size="32px" tile>
                                    <v-icon :class="option.classes">{{ option.icon }}</v-icon>
                                </v-avatar>
                            </v-list-item-avatar>

                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </template>
            </div>
        </v-bottom-sheet>
    </v-footer>
</template>

<script>
import { mapGetters } from 'vuex';
import Shift from '@/models/Shift';
import CashRegister from '@/models/CashRegister';
import EinvoiceService from '@/services/EinvoiceService';
import * as mutationTypes from '@/store/mutationTypes';

export default {
    name: 'OrderFooter',

    data: () => ({
        bottomNav: '',
        showBottomSheet: false
    }),

    computed: {
        ...mapGetters(['curOrder', 'trainingMode', 'pausedOrder']),

        options() {
            const cash = {
                available: true,
                value: 'cash',
                text: this.$t('general.cash'),
                icon: 'fal fa-money-bill',
                disabled: this.paymentTypeDisabled('cash')
            }
            const creditCard = {
                available: true,
                value: 'credit_card',
                text: this.$t('general.credit_card'),
                icon: 'fal fa-credit-card',
                disabled: this.paymentTypeDisabled('credit_card')
            }
            const creditCardBusiness = {
                available: locale === 'me',
                value: 'credit_card_business',
                text: this.$t('general.credit_card_business'),
                icon: 'fal fa-credit-card',
                disabled: this.paymentTypeDisabled('credit_card_business'),
                classes: {}
            }
            const invoice = {
                available: true,
                value: 'invoice',
                text: this.$t('orders.payment_types.invoice'),
                icon: 'fal fa-file-alt',
                disabled: this.paymentTypeDisabled('invoice'),
                classes: {}
            }
            const einvoice = {
                available: this.einvoiceOnly,
                value: 'einvoice',
                text: this.$tc('general.einvoice', 1),
                icon: 'fal fa-file-contract',
                disabled: this.paymentDisabled || !this.einvoiceEnabled,
                classes: {}
            }
            const advance = {
                available: !this.trainingMode && !this.einvoiceOnly,
                value: 'advance',
                text: this.$t('general.advance'),
                icon: 'fal fa-calendar-check',
                disabled: this.paymentTypeDisabled('advance'),
                classes: {}
            }
            const otherPayments = {
                available: !this.einvoiceOnly,
                value: 'other_payments',
                text: this.$t('general.other_payments'),
                icon: 'fal fa-money-check',
                disabled: this.paymentDisabled,
                classes: {}
            }
            const proforma = {
                available: true,
                value: 'proforma',
                text: this.$tc('general.proforma', 1),
                icon: 'fal fa-file-invoice',
                disabled: this.paymentDisabled,
                classes: {}
            }
            const discount = {
                available: true,
                value: 'discount',
                text: this.$t('general.discount'),
                icon: 'fal fa-percent',
                disabled: this.paymentDisabled
            }
            const client = {
                available: true,
                value: 'client',
                text: this.$tc('general.buyer', 2),
                icon: 'fal fa-user-circle',
                disabled: !this.curShift
            }
            const note = {
                available: true,
                value: 'note',
                text: this.$t('general.note'),
                icon: 'fal fa-comment-lines',
                disabled: !this.curOrder,
                classes: {}
            }
            const reference = {
                available: locale === 'sr',
                value: 'reference',
                text: this.$t('general.reference'),
                icon: 'fal fa-link',
                disabled: !this.curOrder,
                classes: {}
            }
            const options = {
                available: true,
                value: 'options',
                text: this.$t('general.options'),
                icon: 'fal fa-ellipsis-h',
                disabled: false
            }
            const pauseOrder = {
                available: true,
                value: 'pause_order',
                text: !this.pausedOrder ? this.$t('general.receipt_pause') : this.$t('general.receipt_continue'),
                icon: !this.pausedOrder ? 'fal fa-pause' : 'fal fa-play',
                disabled: (!!this.curOrder && !!this.pausedOrder) || (!this.curOrder && !this.pausedOrder),
            }
            const cancelOrder = {
                available: true,
                value: 'cancel_order',
                text: this.$t('general.cancel_order'),
                icon: 'fal fa-ban',
                disabled: this.paymentDisabled,
                classes: { 'error--text': !this.paymentDisabled }
            }
            const openDrawer = {
                available: locale !== 'sr' && this.device.platform === 'electron',
                value: 'open_drawer',
                text: this.$t('general.open_drawer'),
                icon: 'fal fa-inbox-out',
                disabled: false,
                classes: {}
            }

            if(this.noncashOnly) {
                if(locale === 'sr') {
                    return {
                        primary: [einvoice, invoice, options, discount, client].filter(o => o.available),

                        secondary: [{
                            title: this.$t('general.other_options'),
                            items: [proforma, note, pauseOrder, cancelOrder].filter(o => o.available)
                        }]
                    }
                }

                return {
                    primary: [invoice, proforma, options, discount, client].filter(o => o.available),

                    secondary: [{
                        title: this.$t('general.payment'),
                        items: [creditCardBusiness, advance, otherPayments].filter(o => o.available)
                    }, {
                        title: this.$t('general.other_options'),
                        items: [note, pauseOrder, cancelOrder].filter(o => o.available)
                    }]
                }
            }

            return {
                primary: [cash, creditCard, options, discount, client].filter(o => o.available),

                secondary: [{
                    title: this.$t('general.payment'),
                    items: [invoice, creditCardBusiness, advance, otherPayments, einvoice].filter(o => o.available)
                }, {
                    title: this.$t('general.other_options'),
                    items: [openDrawer, proforma, note, reference, pauseOrder, cancelOrder].filter(o => o.available)
                }]
            }
        },

        curCashRegister() {
            return CashRegister.current()
        },

        curShift() {
            return Shift.current();
        },

        orderEmpty() {
            return !this.curOrder || !this.curOrder.items || !this.curOrder.items.length;
        },

        paymentDisabled() {
            if(this.orderEmpty || !this.curShift) return true;

            const hasItemsWithZeroPrice = this.curOrder.items.some(item => item.getUnitPrice().toUnit() === 0);
            if(hasItemsWithZeroPrice) return true;

            const hasItemsWithNegativeCount = this.curOrder.items.some(item => item.getCount() < 0);
            const allItemsHavNegativeCounts = this.curOrder.items.every(item => item.getCount() < 0);
            if(hasItemsWithNegativeCount && !allItemsHavNegativeCounts) return true;

            if(this.curOrder.refersToAdvance()) return false

            return this.curOrder.getLeftToPay().toUnit() == 0;
        },

        noncashOnly() {
            return !!this.tenant?.venue?.settings?.noncash_only;
        },

        einvoiceEnabled() {
            return EinvoiceService.isEnabled();
        },

        einvoiceOnly() {
            return EinvoiceService.einvoiceOnly();
        },
    },

    watch: {
        showBottomSheet(val) {
            this.eventBus.$emit('show-screens', { bottomSheet: val });
        }
    },

    methods: {
        paymentTypeDisabled(type) {
            if(!this.curCashRegister) return true;

            if(this.paymentDisabled) return true;

            if(this.noncashOnly && ['cash', 'credit_card'].includes(type)) return true;

            if(type === 'advance' && this.curOrder?.refersToAdvance()) return true;

            if(this.curOrder?.refersToAdvance() && this.curOrder.referredAdvanceOrders()[0].isEinvoice()) return true;

            return !this.curCashRegister.allowsPayment(type);
        },

        selectOption(option) {
            if(['cash', 'credit_card', 'invoice', 'credit_card_business', 'order', 'advance'].includes(option)) {
                this.payAction(option);
            }

            if(option === 'options') this.showBottomSheet = true;
            if(option === 'discount') this.showOrderDiscount();
            if(option === 'client') this.selectClient();
            if(option === 'other_payments') this.showPaymentMethods();
            if(option === 'proforma') this.showInvoiceProformaDialog();
            if(option === 'einvoice') this.showEinvoiceDialog();
            if(option === 'note') this.addNote();
            if(option === 'reference') this.showOrderReferenceDialog();
            if(option === 'cancel_order') this.cancelOrder();
            if(option === 'pause_order') this.pauseOrder();
            if(option === 'open_drawer') this.openDrawer();
        },

        payAction(method) {
            this.showBottomSheet = false;

            if(!this.curShift || this.orderEmpty) return

            if(this.curCashRegister.depositRequired()) {
                this.eventBus.$emit('cash-register-deposit')
                return
            }

            if(['credit_card_business', 'order'].includes(method)) {
                this.eventBus.$emit('order-init-action', { paymentType: method });
            }

            if(method === 'cash') {
                const addToDebtEnabled = locale === 'sr' && !this.curCashRegister.allowsPayment('credit_card');
                if(addToDebtEnabled || this.tenant.venue.settings.cash_change_enabled) {
                    this.eventBus.$emit('order-toggle-dialog', { dialog: 'cash' })
                } else {
                    this.eventBus.$emit('order-init-action', { paymentType: method });
                }
            }

            if(method === 'credit_card') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'creditCard' })
            }

            if(method === 'invoice') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'invoice' })
            }

            if(method === 'advance') {
                this.eventBus.$emit('order-toggle-dialog', { dialog: 'advance' })
            }
        },

        cancelOrder() {
            if(!this.curShift || this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-cancel-order');
        },

        pauseOrder() {
            if(!this.curShift) return

            if(this.curOrder && !this.pausedOrder) {
                this.eventBus.$emit('order-pause-order', true);
            } else if (!this.curOrder && this.pausedOrder) {
                this.eventBus.$emit('order-pause-order', false);
            }

            this.showBottomSheet = false;
        },

        showOrderDiscount() {
            if(!this.curShift || this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'discount' });
        },

        showPaymentMethods() {
            if(!this.curShift || this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'paymentMethods' });
        },

        showOrderReferenceDialog() {
            if(!this.curShift || this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'orderReference' });
        },

        showInvoiceProformaDialog() {
            if(!this.curShift || this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'invoiceProforma' });
        },

        showEinvoiceDialog() {
            if(this.orderEmpty) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'einvoice' });
        },

        selectClient() {
            if(!this.curShift) return

            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'selectClient' });
        },

        openDrawer() {
            this.showBottomSheet = false;

            if(window.electron) {
                this.$printService.openDrawer()
            }
        },

        addNote() {
            this.showBottomSheet = false;
            this.eventBus.$emit('order-toggle-dialog', { dialog: 'note' });
        },
    }
};
</script>

<style lang="scss" scoped>
.order-footer {
    padding: 0px;
    z-index: 4;

    .summary {
        width: 100%;
        color: #282c38;
        background: #e4e6ee;
        border-top: 1px solid #c7cbd6;

        .summary-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 10px;
            min-height: 40px;
            border-bottom: 1px solid #c7cbd6;
            font-weight: 500;
            text-align: right;

            .to-pay {
                font-size: 26px;
            }

            .advance {
                font-size: 16px;
                color: var(--v-error-base);
                margin-bottom: 4px;
            }

            &:last-child {
                border: none;
            }
        }
    }

    .bottom-nav-item {
        max-width: 100% !important;
        min-width: 60px;
    }
}

.order-options {
    min-height: 200px;
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
    background: white;

    .v-list {
        min-width: 200px;

        .bottom-sheet-item {
            height: 55px;
            padding: 0 0 0 10px;

            .icon-disabled {
                opacity: 0.5;
            }
        }
    }
}
</style>
